import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FormField,
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSelect,
} from 'components/formKit'
import type { IArrayOption } from 'components/formKit/fields/FieldNumberArray/FieldNumberArray'
import FieldNumberArray from 'components/formKit/fields/FieldNumberArray/FieldNumberArray'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFieldShadow } from 'services/Branding/components'
import {
  useBrandColorOptions,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import indentsHint from './assets/indentsHint.svg'
import type { SliderFormBrandType } from './sliderTypes'
import { ArrowPosition } from './sliderTypes'

const OFFSET_ARRAY: IArrayOption[] = [
  {
    icon: (
      <Tooltip overlay={t('input.tooltip.leftMargin')}>
        <Icon name='builderInputBack' />
      </Tooltip>
    ),
  },
  {
    icon: (
      <Tooltip overlay={t('input.tooltip.rightMargin')}>
        <Icon name='builderInputForward' />
      </Tooltip>
    ),
  },
  {
    icon: (
      <Tooltip overlay={t('input.tooltip.bottomMargin')}>
        <Icon name='builderInputDown' />
      </Tooltip>
    ),
  },
]

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.slider.style, {
  icons: {
    sliderPosition: genBrandField({
      as: FieldSelect<ArrowPosition>,
      label: t('elements.slider.schema.iconPosition'),
      props: {
        fluid: true,
        options: [
          { label: t('input.option.slide'), value: ArrowPosition.slide },
          { label: t('input.option.progress'), value: ArrowPosition.progress },
        ],
      },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.slider.schema.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  leftArrow: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.slider.schema.prevIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  rightArrow: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.slider.schema.nextIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  activeColor: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.slider.schema.activePoint'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  inactiveColor: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.slider.schema.inactivePoint'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  progress: {
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, max: 48, message: ' ' }],
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      rules: [{ min: 1, max: 48, message: ' ' }],
      props: { width: 64 },
    }),
    marginLeft: genBrandField({
      as: FieldNumber,
      label: t('elements.slider.schema.spacing'),
      rules: [{ min: 1, max: 48, message: ' ' }],
      props: { width: 64 },
    }),
  },
  indents: {
    margin: genBrandField({
      as: FieldNumberArray,
      label: t('elements.slider.schema.progressIndents'),
      layout: 'vertical',
      description: <img alt='' src={indentsHint} />,
      props: { options: OFFSET_ARRAY },
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.slider.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const SliderFormStyle: SliderFormBrandType = ({ device, subConfigs, edited, element, setForm }) => {
  const { font } = element || {}
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)
  const values = useElementFontValues('slider', device, font)
  const styles = useElementFontCss('slider', device, font)

  const handleSetTypography = useCallback(() => setForm?.('base'), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.slider.tags.icons')}</Typography>
        <FormField config={schema.icons.sliderPosition} />
        <FormField config={schema.icons.backgroundColor} />
        <FormField config={schema.icons.color} />
        <FormField config={schema.leftArrow.backgroundImage} />
        <FormField config={schema.rightArrow.backgroundImage} />

        <Typography weight='medium'>{t('elements.slider.tags.progress')}</Typography>
        <FormField config={schema.activeColor.backgroundColor} />
        <FormField config={schema.inactiveColor.backgroundColor} />
        <FormField config={schema.progress.height} />
        <FormField config={schema.progress.width} />
        <FormField config={schema.progress.marginLeft} />
        <FormField config={schema.indents.margin} />

        <Typography weight='medium'>{t('elements.slider.tags.hover')}</Typography>
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <BrandButtonFont
          edited={edited?.font?.base}
          font='base'
          onClick={handleSetTypography}
          styles={styles.base}
          values={values.base}
        >
          {t('page.brand.tab.typography')}
        </BrandButtonFont>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(SliderFormStyle) as typeof SliderFormStyle
